import { Search, X } from "lucide-react";
import * as React from "react";
import { cn } from "@/shared/lib/utils";

/** Keresőmező: 48 px, teljes kör, `search` ikonnal (design-rendszer 7., listák). */
export function SearchInput({
  value,
  onChange,
  placeholder = "Keresés",
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  label: string;
}) {
  return (
    <div className="relative">
      <Search
        size={18}
        className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted"
      />
      <input
        type="search"
        aria-label={label}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-full border border-line bg-white pl-11 pr-10 text-[15px] text-ink placeholder:text-placeholder focus-visible:border-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40 [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          aria-label="Keresés törlése"
          onClick={() => onChange("")}
          className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-muted"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
}

export interface FilterOption<T extends string | number> {
  value: T;
  label: string;
}

/** Vízszintesen görgethető szűrő-chipek a lista tetején. */
export function FilterChips<T extends string | number>({
  options,
  value,
  onChange,
  allLabel = "Mind",
  label,
}: {
  options: FilterOption<T>[];
  value: T | "";
  onChange: (v: T | "") => void;
  allLabel?: string;
  label: string;
}) {
  const all: Array<FilterOption<T> | { value: ""; label: string }> = [
    { value: "", label: allLabel },
    ...options,
  ];
  return (
    <div role="group" aria-label={label} className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-0.5 md:mx-0 md:px-0">
      {all.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value as T | "")}
            className={cn(
              "shrink-0 rounded-chip px-2.5 py-1.5 text-[11px] font-semibold transition-colors",
              active ? "bg-gold-soft text-gold-text" : "border border-line text-muted hover:bg-paper",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Szegmentált vezérlő (állapot-választó): egyenlő lapok, aktív fehér + árnyék. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: FilterOption<T>[];
  value: T | null;
  onChange: (v: T | null) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex gap-1 rounded-md bg-gray p-1">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? null : o.value)}
            className={cn(
              "flex h-9 flex-1 items-center justify-center rounded-sm px-1 text-center text-[12px] leading-tight transition-colors",
              active ? "bg-white font-semibold text-ink shadow-segment" : "font-medium text-muted",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Szűrő-sor: legördülők egymás mellett, keskeny kijelzőn tördelve (design-rendszer 7.). */
export function FilterRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap gap-2">{children}</div>;
}

/** Egy szűrő-legördülő: „Minden …" alapérték + a lehetséges értékek. A hosszabb feliratú,
 *  soklistás szűrőknél ez váltja a chipeket (fejezet, típus, hozzáállás, saját jelölés).
 *  `allLabel` nélkül nincs üres opció — annak, aminek mindig van értéke (pl. rendezés). */
export function FilterSelect<T extends string | number>({
  label,
  allLabel,
  options,
  value,
  onChange,
}: {
  label: string;
  allLabel?: string;
  options: FilterOption<T>[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Select
      aria-label={label}
      className="min-w-40 flex-1"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {allLabel !== undefined && <option value="">{allLabel}</option>}
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </Select>
  );
}

export const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, ...props }, ref) => (
  <select
    ref={ref}
    className={cn(
      "h-12 w-full rounded-md border border-line bg-white px-3 text-[15px] text-ink focus-visible:border-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40",
      className,
    )}
    {...props}
  />
));
Select.displayName = "Select";
